import { Injectable } from '@angular/core';
import { ApiHelper } from './apiHelper';
import { ConstantMan } from './constantMan';
import { Observable, Subject } from 'rxjs';
import { UserDao } from './dao/userDao';
import { User } from '../models/user';
import { HttpHeaders } from '@angular/common/http';

interface LoginResponse {
  user: User;
  access_token: string;
}

@Injectable()
export class AuthService {
  static PATHS = {
    SEND_MESSAGE: 'sendMessage'
  };

  private userIsAuthenticatedSubject = new Subject<boolean>();
  private userIsNewSubject = new Subject<string>();
  private loggedIn: boolean = false;

  constructor(private apiHelper: ApiHelper, private userDao: UserDao) {
    this.userIsAuthenticatedSubject.next(false);
  }

  refreshLogin() {
    let token = this.apiHelper.getAccessToken();
    if (token) {
      this.apiHelper.setAccessToken(token);
      this.loggedIn = true;
      this.checkSession().subscribe(
        res => {
          let user = res;
          this.apiHelper.setAccessToken(token);
          this.userDao.setCurrentUser(user);
          this.loggedIn = true;
          this.userIsAuthenticatedSubject.next(this.loggedIn);
        },
        err => console.error(err)
      );
    }
  }

  login(email: string, password: string): Subject<boolean> {
    let url = this.apiHelper.getServiceName() + ConstantMan.API.RESOURCE.LOGIN;
    let method = 'POST';
    let httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data = {
      grant_type: 'password',
      username: email,
      password: password
    };

    let requestObservable: Observable<LoginResponse> = this.apiHelper.apiCall<LoginResponse>(
      url,
      method,
      null,
      data,
      httpHeader,
      null
    );
    requestObservable.subscribe((data: LoginResponse) => {
      let user = data.user;
      let token = data.access_token;
      this.apiHelper.setAccessToken(token);
      this.userDao.setCurrentUser(user);
      this.loggedIn = true;
      this.userIsAuthenticatedSubject.next(this.loggedIn);
    });

    return this.userIsAuthenticatedSubject;
  }

  tokenSet(email: string, password: string): Subject<string> {
    let url = this.apiHelper.getServiceName() + ConstantMan.API.RESOURCE.LOGIN;
    let method = 'POST';
    let httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data = {
      grant_type: 'password',
      username: email,
      password: password
    };

    let requestObservable: Observable<LoginResponse> = this.apiHelper.apiCall<LoginResponse>(
      url,
      method,
      null,
      data,
      httpHeader,
      null
    );
    requestObservable.subscribe((data: LoginResponse) => {
      let token = data.access_token;
      this.apiHelper.setAccessToken(token);
      this.userIsNewSubject.next('tokenSet');
    });
    return this.userIsNewSubject;
  }

  logout(): Observable<any> {
    let url = this.apiHelper.getServiceName() + ConstantMan.API.RESOURCE.LOGOUT;
    let method = 'POST';
    let httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data = {};

    const authSvc = this;

    const requestObservable = this.apiHelper.apiCall(url, method, null, data, httpHeader, null);
    return new Observable(function subscribe(observer) {
      requestObservable.subscribe(
        data => {
          authSvc.apiHelper.removeAccessToken();
          observer.next(data);
          observer.complete();
          authSvc.loggedIn = false;
          authSvc.userIsAuthenticatedSubject.next(authSvc.loggedIn);
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  checkSession(): Observable<any> {
    let url = this.apiHelper.getServiceName() + ConstantMan.API.RESOURCE.SESSION;
    let method = 'GET';
    let httpHeader: HttpHeaders = this.apiHelper.getDefaultHeader();

    let data = {};

    let sessionObservable = new Observable(observer => {
      let requestObservable: Observable<User> = this.apiHelper.apiCall<User>(url, method, null, data, httpHeader, null);
      requestObservable.subscribe(
        (user: User) => {
          this.userDao.setCurrentUser(user as User);
          observer.next(user);
          if (!this.loggedIn) {
            this.loggedIn = true;
            this.userIsAuthenticatedSubject.next(this.loggedIn);
          }
          observer.complete();
        },
        err => {
          this.loggedIn = false;
          this.userIsAuthenticatedSubject.next(this.loggedIn);
        }
      );
    });

    return sessionObservable;
  }

  sendMessage(subject: string, message: string): Observable<any> {
    const url: string = this.apiHelper.getServiceName() + AuthService.PATHS.SEND_MESSAGE;
    const method: string = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const body = {
      subject: subject,
      message: message,
      key: ConstantMan.TOKENS.SEND_MESSAGE_KEY
    };

    const httpRequest: Observable<any> = this.apiHelper.apiCall<any>(url, method, null, body, headers);

    return new Observable(observer => {
      httpRequest.subscribe(
        response => {
          observer.next(response);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  isLoggedIn(): boolean {
    return this.loggedIn;
  }

  getSessionAuthenticatedSubject(): Subject<boolean> {
    return this.userIsAuthenticatedSubject;
  }
}
